import { useEffect, useState } from 'react';
import { useSearch } from 'wouter';
import { affiliateApplicationSchema, BUSINESS } from '@mopani/contracts';
import { Hero, Section } from '../components/ui/primitives';
import { Field } from '../components/ui/Field';
import { ApiRequestError, lookupReferralCode, submitAffiliateApplication } from '../lib/api';
import { PARTNER_PORTAL_URL } from '../lib/partnerPortal';
import { usePageMeta } from '../lib/seo';

/**
 * Become a Partner — public affiliate/partner programme application.
 *
 * Explains the two-tier affiliate programme (MS-V2-AFF-001 — see
 * @mopani/contracts affiliate.ts / affiliateApi.ts) and collects applications.
 * Deliberately does NOT publish commission percentages or rand figures: those
 * are commercial policy owned by packages/contracts, not marketing copy, and
 * the board has already revisited them once mid-project (L1_LADDER_BANDS vs
 * L1_FLAT_BANDS in affiliate.ts). Printing a number on this page would make it
 * wrong the next time the board changes a rate.
 *
 * THIS IS AN APPLICATION, NOT ACCOUNT CREATION. There is no self-serve signup
 * — Mopani staff review every application and issue portal credentials by
 * hand. That is stated plainly in the copy and again next to the submit
 * button, so nobody submits expecting an instant dashboard. Saying otherwise
 * would repeat the defect documented at the top of Quote.tsx: a form that
 * promises something the system does not actually do.
 *
 * Same submit/error/loading contract as Quote.tsx:
 *   - validates client-side against the SHARED contract schema
 *     (affiliateApplicationSchema), so this form and the API cannot disagree
 *   - shows the success panel ONLY on a real success response from the API,
 *     and only then displays the server-returned reference
 *   - surfaces server-returned field errors
 *   - sends the honeypot the backend expects, in the exact shape Quote.tsx
 *     already uses (see affiliateApplicationSchema's comment on `website` for
 *     why it must validate rather than 400 on a filled honeypot)
 *
 * Referral handling: a `?ref=CODE` query param (from an existing affiliate's
 * own referral link) prefills `referredByCode` and is optionally resolved via
 * GET /api/affiliate/referral/:code to show "Referred by {firstName}". That
 * lookup fails quietly — an unreachable or invalid code must never block
 * submission, it only loses the confirmation message. The code itself still
 * flows through to the submitted payload either way, since that is what
 * creates the tier-2 sponsor relationship on approval.
 *
 * "Already a partner? Sign in" links to PARTNER_PORTAL_URL (see
 * lib/partnerPortal.ts) so an already-approved partner who lands here has a
 * route back to the portal instead of re-reading an application form aimed at
 * new applicants. Deliberately a quiet secondary link, not a second CTA next
 * to Submit Application — that stays the one loud action on this page.
 */

const PROGRAMME_POINTS = [
  {
    title: 'You Refer',
    body: 'Introduce schools, clinics or government offices you already know to Mopani Suppliers, using your own referral link.',
  },
  {
    title: 'They Order, You Earn',
    body: 'When a school, clinic or office you referred places and pays for an order, you earn a commission on the profit of that order.',
  },
  {
    title: 'Grow the Network',
    body: 'Recruit other partners into the programme and earn a smaller override on the orders their referrals place too — a two-tier structure that rewards helping the network grow, not only your own referrals.',
  },
] as const;

const APPLY_STEPS = [
  {
    title: 'Submit Your Application',
    body: 'Tell us a little about yourself and, if you have one, who referred you.',
  },
  {
    title: 'We Review It',
    body: 'Our team checks every application by hand. There is no instant account creation.',
  },
  {
    title: 'Receive Your Login',
    body: 'Once approved, we email you credentials for the partner portal, where you get your referral link and track what you have earned.',
  },
] as const;

const NEXT_STEPS = [
  'We review your application and the details you have provided.',
  'We may contact you by phone, email or WhatsApp to confirm a few details.',
  'If approved, we email you secure login credentials for the partner portal — there is no self-serve signup.',
  'You receive your personal referral link and can start introducing schools, clinics and offices.',
  'You earn commission when a referral places and pays for an order, plus a smaller override on orders from partners you recruit.',
] as const;

type FieldErrors = Record<string, string | undefined>;

export function Partners() {
  const search = useSearch();
  const prefillRef = new URLSearchParams(search).get('ref') ?? '';

  usePageMeta({
    title: 'Become a Partner',
    description:
      'Join the Mopani Suppliers partner programme. Refer schools, clinics and government offices in the Mopani region and earn commission on the orders they place.',
    path: '/partners',
  });

  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [reference, setReference] = useState<string | null>(null);

  // Optional confirmation only — a failed or invalid lookup must never block
  // the form, so errors here are swallowed rather than surfaced.
  useEffect(() => {
    if (!prefillRef) return;
    let cancelled = false;
    lookupReferralCode(prefillRef)
      .then((res) => {
        if (!cancelled && res.valid) setReferrerName(res.affiliateFirstName);
      })
      .catch(() => {
        // Fail quietly — see file header.
      });
    return () => {
      cancelled = true;
    };
  }, [prefillRef]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setFormError(null);

    const fd = new FormData(e.currentTarget);
    const input = {
      fullName: String(fd.get('fullName') ?? ''),
      email: String(fd.get('email') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      company: String(fd.get('company') ?? ''),
      motivation: String(fd.get('motivation') ?? ''),
      referredByCode: String(fd.get('referredByCode') ?? ''),
      website: String(fd.get('website') ?? ''),
    };

    const parsed = affiliateApplicationSchema.safeParse(input);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors(Object.fromEntries(Object.entries(flat).map(([k, v]) => [k, v?.[0]])));
      setFormError('Please check the highlighted fields.');
      return;
    }

    setStatus('submitting');
    try {
      const res = await submitAffiliateApplication(parsed.data);
      setReference(res.reference ?? null);
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      if (err instanceof ApiRequestError) {
        if (err.fieldErrors) {
          setErrors(Object.fromEntries(Object.entries(err.fieldErrors).map(([k, v]) => [k, v[0]])));
        }
        setFormError(
          err.status === 429
            ? 'You have sent several applications recently. Please wait a few minutes, or contact us directly.'
            : `${err.message} You can also reach us on ${BUSINESS.phoneDisplay}.`,
        );
      } else {
        setFormError(`Something went wrong. Please call or WhatsApp us on ${BUSINESS.phoneDisplay}.`);
      }
    }
  }

  return (
    <>
      <Hero
        title="Become a Partner"
        subtitle="Refer schools, clinics and government offices to Mopani Suppliers and earn commission on the orders they place."
        size="sm"
        padding="pt-16 pb-12"
      />

      <Section
        variant="white"
        title="How the Programme Works"
        intro="A straightforward, two-tier referral programme — you are paid for the relationships you already have, and for helping the network grow."
      >
        <div className="grid gap-8 sm:grid-cols-3">
          {PROGRAMME_POINTS.map((p) => (
            <div key={p.title} className="card p-6">
              <h3 className="font-heading text-xl font-bold text-navy">{p.title}</h3>
              <p className="mt-3 text-charcoal/80">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section variant="muted" title="How to Apply">
        <ol className="grid gap-8 md:grid-cols-3">
          {APPLY_STEPS.map((s, i) => (
            <li key={s.title}>
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full bg-green-cta font-heading text-lg font-bold text-white"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-charcoal/80">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <section className="bg-white px-4 pb-20 pt-4">
        <div className="container-content grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="card p-8">
            <h2 className="font-heading text-xl font-bold text-navy">Partner Application</h2>
            {/* Quiet secondary link, not a competing CTA — see file header. */}
            <p className="mt-1 text-sm text-charcoal/70">
              Already a partner?{' '}
              <a href={PARTNER_PORTAL_URL} className="font-semibold text-green-accent hover:underline">
                Sign in
              </a>{' '}
              to the partner portal.
            </p>

            {status === 'sent' ? (
              /* Shown ONLY after a real success response from the API — see file header. */
              <div
                className="mt-8 rounded-lg border border-green-cta/40 bg-green-cta/5 p-8 text-center"
                role="status"
              >
                <h3 className="font-heading text-2xl font-bold text-navy">
                  Application received
                </h3>
                <p className="mx-auto mt-4 max-w-lg text-charcoal/80">
                  Thank you for applying. Our team will review your application and, if approved,
                  email you login credentials for the partner portal. We do not create accounts
                  automatically.
                </p>
                {reference && (
                  <p className="mt-6 text-sm text-charcoal/70">
                    Your reference:{' '}
                    <code className="rounded bg-muted px-2 py-1 font-mono text-charcoal">
                      {reference.slice(0, 8).toUpperCase()}
                    </code>
                  </p>
                )}
                <p className="mt-6 text-sm text-charcoal/70">
                  Questions in the meantime?{' '}
                  <a
                    href={BUSINESS.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-green-accent hover:underline"
                  >
                    WhatsApp us on {BUSINESS.phoneDisplay}
                  </a>
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-6 space-y-6">
                {formError && (
                  <div
                    className="field-error rounded border border-[#c0392b]/30 bg-[#c0392b]/5 p-3"
                    role="alert"
                  >
                    {formError}
                  </div>
                )}

                <Field name="fullName" label="Full Name" required error={errors.fullName}>
                  {(p) => <input type="text" placeholder="Your full name" autoComplete="name" {...p} />}
                </Field>

                <Field name="email" label="Email Address" required error={errors.email}>
                  {(p) => (
                    <input type="email" placeholder="name@example.com" autoComplete="email" {...p} />
                  )}
                </Field>

                <Field
                  name="phone"
                  label="Phone / WhatsApp"
                  required
                  error={errors.phone}
                  hint="We may contact you on WhatsApp to confirm your application."
                >
                  {(p) => <input type="tel" placeholder="063 996 0861" autoComplete="tel" {...p} />}
                </Field>

                <Field name="company" label="Company / Organisation" error={errors.company}>
                  {(p) => (
                    <input type="text" placeholder="If applicable" autoComplete="organization" {...p} />
                  )}
                </Field>

                <Field
                  name="motivation"
                  label="Why do you want to become a partner?"
                  error={errors.motivation}
                >
                  {(p) => (
                    <textarea
                      rows={4}
                      placeholder="Tell us about the schools, clinics or offices you can introduce us to."
                      {...p}
                    />
                  )}
                </Field>

                <div>
                  <Field
                    name="referredByCode"
                    label="Referral Code"
                    error={errors.referredByCode}
                    // Only claim a prefill happened when one actually did — with no
                    // ?ref= the field is empty and this hint would otherwise assert
                    // something false.
                    hint={
                      prefillRef
                        ? 'If another partner referred you, their code is prefilled here from your referral link.'
                        : undefined
                    }
                  >
                    {(p) => (
                      // key={prefillRef}: this input is uncontrolled (defaultValue).
                      // Without a key, following a second referral link via
                      // client-side navigation leaves the stale first code in the
                      // field while "Referred by" below updates to the new name.
                      // The key forces a remount so defaultValue re-applies.
                      <input
                        key={prefillRef}
                        type="text"
                        defaultValue={prefillRef}
                        placeholder="Optional"
                        {...p}
                      />
                    )}
                  </Field>
                  {referrerName && (
                    // aria-live: this confirmation appears asynchronously after the
                    // referral lookup resolves, well after the field's own
                    // aria-describedby is read — without a live region screen
                    // reader users never learn it appeared.
                    <p className="mt-1 text-sm text-green-accent" aria-live="polite">
                      Referred by {referrerName}.
                    </p>
                  )}
                </div>

                {/* Honeypot — same shape Quote.tsx uses; the backend implements the same contract. */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="field-website">Leave this field empty</label>
                  <input id="field-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                </div>

                <div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-hero w-full"
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? 'Submitting…' : 'Submit Application'}
                  </button>
                  <p className="mt-3 text-center text-sm text-charcoal/60">
                    This is an application, not an account. Our team reviews every submission by
                    hand — if approved, we will email you login credentials for the partner portal.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* "What Happens Next?" navy panel — mirrors Quote.tsx's layout. */}
          <aside className="h-fit rounded-lg bg-navy p-8 text-white">
            <h2 className="font-heading text-2xl font-bold text-white">What Happens Next?</h2>
            <ol className="mt-6 space-y-5">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex gap-4">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-cta font-heading text-sm font-bold text-white"
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                  <p className="text-sm text-white/85">{s}</p>
                </li>
              ))}
            </ol>
            <div className="mt-8 border-t border-white/15 pt-6">
              <p className="text-sm font-medium italic text-white/85">
                Questions before you apply?
                <br />
                <a
                  href={BUSINESS.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-green-cta hover:underline"
                >
                  WhatsApp us on {BUSINESS.phoneDisplay}
                </a>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
